import React from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { apiFetch } from "../lib/api";
import SequenceBoard from "../components/SequenceBoard.jsx";
import CampaignStepsTutorial from "../components/CampaignStepsTutorial.jsx";
import "./../styles/Campaigns.css";
import SEO from "../components/SEO.jsx";
import { seoPresets } from "../seoPresets";

async function apiGetSteps(campaignId) {
  const j = await apiFetch(`/api/email/campaigns/${campaignId}/steps`, { credentials: "include" });
  return Array.isArray(j.items) ? j.items : [];
}
async function apiSaveSteps(campaignId, steps) {
  return apiFetch(`/api/email/campaigns/${campaignId}/steps`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ steps }),
    credentials: "include",
  });
}
async function apiListTemplates() {
  const j = await apiFetch("/api/email/templates", { credentials: "include" });
  return Array.isArray(j.items) ? j.items : [];
}
async function apiCreateTemplate({ name, subject, html, text = null }) {
  const j = await apiFetch("/api/email/templates", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name, subject, html, text }),
  });
  return j?.id;
}
async function apiGetCampaign(campaignId) {
  const j = await apiFetch(`/api/email/campaigns/${campaignId}`, {
    credentials: "include",
  });
  return j?.item;
}

async function apiUpdateCampaign(campaignId, data) {
  const j = await apiFetch(`/api/email/campaigns/${campaignId}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(data),
  });
  return j?.item;
}

async function apiListRecipients(
  campaignId,
  { q = "", stage = "", limit = 200, offset = 0 } = {}
) {
  const params = new URLSearchParams();
  if (q.trim()) params.set("q", q.trim());
  if (stage) params.set("stage", stage);
  params.set("limit", String(limit));
  params.set("offset", String(offset));
  const qs = params.toString();

  const j = await apiFetch(
    `/api/email/campaigns/${campaignId}/recipients${qs ? `?${qs}` : ""}`,
    { credentials: "include" }
  );
  return j;
}

async function apiDeleteRecipient(recipientId) {
  return apiFetch(`/api/email/recipients/${recipientId}`, {
    method: "DELETE",
    credentials: "include",
  });
}

async function apiGetRecipientEvents(recipientId) {
  const j = await apiFetch(`/api/email/recipients/${recipientId}/events`, {
    credentials: "include",
  });
  return Array.isArray(j.items) ? j.items : [];
}

const DEFAULT_TEMPLATE_NAME = "Default Outreach";
const DEFAULT_TEMPLATE_SUBJECT = "Feature request for {{playlist_name}}";
const DEFAULT_TEMPLATE_HTML = `<p>Hi {{curator_name}},</p><p>I came across your playlist <a href="{{playlist_url}}">{{playlist_name}}</a> and thought this release could be a great fit.</p><p>Best,<br>{{sender_name}}</p>`;
const DEFAULT_TEMPLATE_TEXT = `Hi {{curator_name}}, I came across your playlist {{playlist_name}} ({{playlist_url}}) and thought this release could be a great fit. Best, {{sender_name}}`;

export default function CampaignSteps() {
  const seo = seoPresets["/campaign/:id/steps"];
  const { id } = useParams();
  const navigate = useNavigate();
  const campaignId = Number(id);

  const [templates, setTemplates] = React.useState([]);
  const [tplLoading, setTplLoading] = React.useState(false);
  const [tplErr, setTplErr] = React.useState("");

  const [hasUnsaved, setHasUnsaved] = React.useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = React.useState(false);
  const pendingNavRef = React.useRef(null);
  const dirtyRef = React.useRef(false);
  const boardRef = React.useRef(null);

  // NEW: campaign meta (for name / settings)
  const [campaign, setCampaign] = React.useState(null);
  const [campLoading, setCampLoading] = React.useState(true);
  const [campErr, setCampErr] = React.useState("");
  const [nameDraft, setNameDraft] = React.useState("");
  const [savingName, setSavingName] = React.useState(false);
  const [nameMsg, setNameMsg] = React.useState("");

  // NEW: which tab is visible: settings / editor / contacts
  const [tab, setTab] = React.useState("editor"); // default to Editor

  // NEW: show steps tutorial
  const [showStepsTutorial, setShowStepsTutorial] = React.useState(false);

  React.useEffect(() => {
    let ignore = false;
    (async () => {
      setTplLoading(true);
      setTplErr("");
      try {
        let items = await apiListTemplates();
        let def = items.find(
          (t) => (t.name || "").toLowerCase() === DEFAULT_TEMPLATE_NAME.toLowerCase()
        );
        if (!def) {
          const newId = await apiCreateTemplate({
            name: DEFAULT_TEMPLATE_NAME,
            subject: DEFAULT_TEMPLATE_SUBJECT,
            html: DEFAULT_TEMPLATE_HTML,
            text: DEFAULT_TEMPLATE_TEXT,
          });
          def = {
            id: newId,
            name: DEFAULT_TEMPLATE_NAME,
            subject: DEFAULT_TEMPLATE_SUBJECT,
            html: DEFAULT_TEMPLATE_HTML,
            text: DEFAULT_TEMPLATE_TEXT,
          };
          items = [def, ...items];
        } else {
          items = [def, ...items.filter((x) => x.id !== def.id)];
        }
        if (!ignore) setTemplates(items);
      } catch (e) {
        if (!ignore) setTplErr(e?.message || "Failed to load templates");
      } finally {
        if (!ignore) setTplLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, []);

  // NEW: load campaign meta for Settings tab
  React.useEffect(() => {
    let ignore = false;
    (async () => {
      setCampLoading(true);
      setCampErr("");
      try {
        const item = await apiGetCampaign(campaignId);
        if (!ignore) {
          setCampaign(item);
          setNameDraft(item?.name || "");
        }
      } catch (e) {
        if (!ignore) setCampErr(e?.message || "Failed to load campaign");
      } finally {
        if (!ignore) setCampLoading(false);
      }
    })();
    return () => {
      ignore = true;
    };
  }, [campaignId]);

  // NEW: show tutorial once, after things are loaded
  React.useEffect(() => {
    const done =
      localStorage.getItem("campaignStepsTutorialCompleted") === "true";
    if (!done && !tplLoading && !campLoading) {
      setShowStepsTutorial(true);
    }
  }, [tplLoading, campLoading]);

  // Warn on browser/tab close with unsaved changes
  React.useEffect(() => {
    const beforeUnload = (e) => {
      if (!hasUnsaved) return;
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [hasUnsaved]);

  function goBack() {
    if (hasUnsaved) {
      setShowLeaveConfirm(true);
      pendingNavRef.current = () => navigate("/campaign");
    } else {
      navigate("/campaign");
    }
  }

  async function saveName() {
    if (!campaign || !nameDraft.trim()) return;
    setSavingName(true);
    setNameMsg("");
    try {
      const updated = await apiUpdateCampaign(campaignId, {
        name: nameDraft.trim(),
      });
      setCampaign(updated);
      setNameDraft(updated.name || "");
      setNameMsg("Saved.");
      setTimeout(() => setNameMsg(""), 1800);
    } catch (e) {
      setNameMsg(e?.message || "Failed to save name");
    } finally {
      setSavingName(false);
    }
  }

  const navButton = (value, label) => (
    <button
      key={value}
      data-tour={
        value === "settings"
          ? "tab-settings"
          : value === "editor"
            ? "tab-editor"
            : value === "contacts"
              ? "tab-contacts"
              : undefined
      }
      className={"psl-btn" + (tab === value ? " primary" : "")}
      style={{ padding: "6px 12px", borderRadius: 999, fontSize: 13 }}
      onClick={() => setTab(value)}
      type="button"
    >
      {label}
    </button>
  );

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        image={seo.openGraph?.image}
        jsonLd={seo.jsonLd}
        ogType={seo.openGraph?.type || "website"}
        twitterCard={seo.twitter?.card || "summary_large_image"}
        robots="noindex, nofollow"
      />
      <div className="psl-shell no-shell-lock">
        <div className="psl-subnav">
          <div className="psl-brand">
            <span>
              Campaign #{campaignId}
              {campaign?.name ? ` — ${campaign.name}` : " — Steps"}
            </span>
          </div>
          <div
            className="psl-quick"
            style={{
              marginLeft: "auto",
              flex: "0 0 auto",
              justifyContent: "flex-end",
            }}
          >
            <button
              className="psl-btn"
              onClick={() => {
                if (dirtyRef.current) {
                  setShowLeaveConfirm(true);
                  pendingNavRef.current = () => navigate("/campaign");
                } else {
                  navigate("/campaign");
                }
              }}
            >
              ← Back to campaigns
            </button>
          </div>
        </div>

        {/* Tab strip (not real navbar, just buttons) */}
        <div
          style={{
            display: "flex",
            gap: 8,
            padding: "10px 18px 0",
            flexWrap: "wrap",
          }}
        >
          {navButton("settings", "Settings")}
          {navButton("editor", "Editor")}
          {navButton("contacts", "Contacts")}
        </div>

        <div
          className="psl-grid"
          style={{ gridTemplateColumns: "1fr", paddingTop: 8 }}
        >
          <main
            className="psl-main"
            style={{ gridColumn: "1 / -1", minWidth: 0 }}
          >
            {/* SETTINGS TAB */}
            <section
              style={{
                display: tab === "settings" ? "block" : "none",
                marginBottom: 16,
              }}
            >
              <div className="psl-camp-card" style={{ maxWidth: 520 }}>
                {campLoading ? (
                  <p className="psl-muted">Loading campaign…</p>
                ) : campErr ? (
                  <p style={{ color: "#9b1c1c" }}>{campErr}</p>
                ) : campaign ? (
                  <>
                    <h3 style={{ marginTop: 0 }}>Settings</h3>
                    <label className="psl-label" style={{ marginTop: 8 }}>
                      Campaign name
                      <input
                        className="psl-input"
                        value={nameDraft}
                        onChange={(e) => setNameDraft(e.target.value)}
                      />
                    </label>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        gap: 8,
                        marginTop: 14,
                      }}
                    >
                      <button
                        className="psl-btn"
                        type="button"
                        disabled={savingName}
                        onClick={() =>
                          setNameDraft(campaign.name || "")
                        }
                      >
                        Reset
                      </button>
                      <button
                        className="psl-btn primary"
                        type="button"
                        disabled={
                          savingName ||
                          !nameDraft.trim() ||
                          nameDraft.trim() === (campaign.name || "")
                        }
                        onClick={saveName}
                      >
                        {savingName ? "Saving…" : "Save name"}
                      </button>
                    </div>
                    {nameMsg && (
                      <p
                        className="psl-muted"
                        style={{ marginTop: 8, fontSize: 13 }}
                      >
                        {nameMsg}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="psl-muted">Campaign not found.</p>
                )}
              </div>
            </section>

            {/* EDITOR TAB (SequenceBoard) */}
            <section
              style={{
                display: tab === "editor" ? "block" : "none",
                marginBottom: 16,
              }}
            >
              {tplLoading ? (
                <div className="psl-camp-card">Loading templates…</div>
              ) : tplErr ? (
                <div
                  className="psl-camp-card"
                  style={{ color: "#9b1c1c" }}
                >
                  {tplErr}
                </div>
              ) : (
                <SequenceBoard
                  ref={boardRef}
                  campaignId={campaignId}
                  templates={templates}
                  apiGetSteps={apiGetSteps}
                  apiSaveSteps={apiSaveSteps}
                  onDirtyChange={(d) => {
                    dirtyRef.current = d;
                    setHasUnsaved(d);
                  }}
                  onSequenceSaved={() => {window.dispatchEvent(new CustomEvent("campaignTutorial:sequenceSaved")); }}
                />
              )}
            </section>

            {/* CONTACTS TAB */}
            <section
              style={{
                display: tab === "contacts" ? "block" : "none",
                marginBottom: 16,
              }}
            >
              <CampaignContacts campaignId={campaignId} />
            </section>
          </main>
        </div>

        {showLeaveConfirm && (
          <div className="psl-modal-overlay" role="dialog" aria-modal="true">
            <div className="psl-camp-card" style={{ maxWidth: 460 }}>
              <h3 style={{ marginTop: 0 }}>Unsaved changes</h3>
              <p>You have unsaved changes. What would you like to do?</p>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                  justifyContent: "flex-end",
                  marginTop: 10,
                }}
              >
                <button
                  className="psl-btn"
                  onClick={() => setShowLeaveConfirm(false)}
                >
                  Stay
                </button>
                <button
                  className="psl-btn"
                  onClick={() => setShowLeaveConfirm(false)}
                >
                  Keep editing
                </button>
                <button
                  className="psl-btn"
                  onClick={async () => {
                    const ok = await boardRef.current?.saveSequence();
                    if (ok) {
                      setShowLeaveConfirm(false);
                      const go =
                        pendingNavRef.current ??
                        (() => navigate("/campaign"));
                      pendingNavRef.current = null;
                      go();
                    }
                  }}
                >
                  Save & exit
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showStepsTutorial && (
        <CampaignStepsTutorial onClose={() => setShowStepsTutorial(false)} />
      )}
    </>
  );
}

function CampaignContacts({ campaignId }) {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(0);

  const [search, setSearch] = React.useState("");
  const [stage, setStage] = React.useState("");

  const [selected, setSelected] = React.useState(null);
  const [events, setEvents] = React.useState([]);
  const [eventsLoading, setEventsLoading] = React.useState(false);
  const [eventsErr, setEventsErr] = React.useState("");

  const stages = [
    ["", "All stages"],
    ["queued", "Queued"],
    ["emailed", "Sent"],
    ["bounced", "Bounced"],
    ["replied", "Replied"],
    ["negotiating", "Negotiating"],
    ["confirmed", "Confirmed"],
  ];

  async function loadRecipients() {
    setLoading(true);
    setError("");
    try {
      const j = await apiListRecipients(campaignId, {
        q: search,
        stage,
        limit: 200,
        offset: 0,
      });
      const list = Array.isArray(j.items) ? j.items : [];
      setItems(list);
      setTotal(j.total ?? list.length);
    } catch (e) {
      setError(e?.message || "Failed to load contacts");
    } finally {
      setLoading(false);
    }
  }

  React.useEffect(() => {
    loadRecipients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaignId]);

  async function handleView(r) {
    setSelected(r);
    setEvents([]);
    setEventsErr("");
    setEventsLoading(true);
    try {
      const ev = await apiGetRecipientEvents(r.id);
      setEvents(ev);
    } catch (e) {
      setEventsErr(e?.message || "Failed to load activity");
    } finally {
      setEventsLoading(false);
    }
  }

  async function handleDelete(r) {
    if (!window.confirm(`Remove ${r.email} from this campaign?`)) return;
    try {
      await apiDeleteRecipient(r.id);
      setItems((cur) => cur.filter((x) => x.id !== r.id));
      setTotal((t) => Math.max(0, t - 1));
      if (selected && selected.id === r.id) {
        setSelected(null);
        setEvents([]);
      }
    } catch (e) {
      alert(e?.message || "Failed to delete contact");
    }
  }

  return (
    <div className="psl-camp-card">
      <h3 style={{ marginTop: 0 }}>Contacts</h3>

      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          marginBottom: 10,
        }}
      >
        <input
          className="psl-input"
          style={{ maxWidth: 260 }}
          placeholder="Search by email or name…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="psl-input"
          style={{ width: 160 }}
          value={stage}
          onChange={(e) => setStage(e.target.value)}
        >
          {stages.map(([val, label]) => (
            <option key={val || "all"} value={val}>
              {label}
            </option>
          ))}
        </select>
        <button
          className="psl-btn"
          type="button"
          onClick={loadRecipients}
          disabled={loading}
        >
          {loading ? "Loading…" : "Apply"}
        </button>
      </div>

      {error && (
        <div style={{ color: "#b91c1c", marginBottom: 8 }}>{error}</div>
      )}

      <div className="psl-tablewrap" style={{ maxHeight: 320 }}>
        <table className="psl-table">
          <thead>
            <tr>
              <th>Email</th>
              <th>Name</th>
              <th>Stage</th>
              <th>Scheduled</th>
              <th>Sent</th>
              <th>Last activity</th>
              <th style={{ width: 130 }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} style={{ padding: 12 }}>
                  Loading contacts…
                </td>
              </tr>
            )}
            {!loading && items.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  style={{ padding: 12, color: "var(--psl-muted)" }}
                >
                  No contacts yet.
                </td>
              </tr>
            )}
            {!loading &&
              items.map((r) => (
                <tr key={r.id}>
                  <td>{r.email}</td>
                  <td>{r.curator_name || "—"}</td>
                  <td>{r.stage}</td>
                  <td>
                    {r.scheduled_for
                      ? new Date(r.scheduled_for).toLocaleString()
                      : "—"}
                  </td>
                  <td>
                    {r.sent_at
                      ? new Date(r.sent_at).toLocaleString()
                      : "—"}
                  </td>
                  <td>
                    {r.last_event_at
                      ? new Date(r.last_event_at).toLocaleString()
                      : "—"}
                  </td>
                  <td>
                    <button
                      className="psl-btn"
                      type="button"
                      style={{ padding: "4px 8px", fontSize: 12 }}
                      onClick={() => handleView(r)}
                    >
                      Activity
                    </button>{" "}
                    <button
                      className="psl-btn"
                      type="button"
                      style={{ padding: "4px 8px", fontSize: 12 }}
                      onClick={() => handleDelete(r)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {total > items.length && (
        <div
          style={{
            marginTop: 6,
            fontSize: 12,
            color: "var(--psl-muted)",
          }}
        >
          Showing {items.length} of {total} contacts. Use filters to narrow
          down.
        </div>
      )}

      {selected && (
        <div style={{ marginTop: 16 }}>
          <h4 style={{ margin: "0 0 4px" }}>
            Activity for {selected.email}
          </h4>
          {eventsLoading && (
            <p className="psl-muted">Loading activity…</p>
          )}
          {eventsErr && (
            <p style={{ color: "#b91c1c" }}>{eventsErr}</p>
          )}
          {!eventsLoading && !eventsErr && events.length === 0 && (
            <p className="psl-muted">No events yet for this contact.</p>
          )}
          {!eventsLoading && events.length > 0 && (
            <ul
              style={{
                listStyle: "none",
                padding: 0,
                margin: 0,
                fontSize: 13,
              }}
            >
              {events.map((ev) => (
                <li
                  key={ev.id}
                  style={{
                    padding: "4px 0",
                    borderBottom:
                      "1px solid rgba(148,163,184,.35)",
                  }}
                >
                  <strong style={{ textTransform: "capitalize" }}>
                    {ev.type}
                  </strong>{" "}
                  <span className="psl-muted">
                    — {new Date(ev.created_at).toLocaleString()}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
